import { Box, Button, Card, CardActionArea, CardContent, Grid2, Skeleton, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import useApiHook from "../hooks/useApiHook";
import { useEffect, useState } from "react";
import { usePackageModal } from "../store/modal-context";
import ResponsiveDialog from "./_atoms/ResponsiveDialog";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import { useUser } from "../store/user-context";
import config from "../config";
import { App } from "@capacitor/app"
import { Browser } from "@capacitor/browser"
import useAppView from "./platforms/useAppView";

const MODAL_STATUS = {
    loading: 0,
    packageList: 1,
    checkout: 2,
    paying: 3,
    success: 4
}

const PackageCard = ({ name, description, price, id, onClick }) => {
    return <Grid2
        onClick={() => onClick(id)}
        size={{ xs: 6, md: 6 }}
        sx={{
            cursor: "pointer"
        }}
    >
        <Card>
            <CardActionArea>
                <CardContent>
                    <Box textAlign="center">
                        <Typography mb={1} variant='h6' color='primary' fontWeight='bold'>{name}</Typography>
                        <Typography mb={2} variant='body1' color='#444'>{description}</Typography>
                        <Typography color='success' variant='h5' fontWeight="bold">€{price}</Typography>
                    </Box>
                </CardContent>
            </CardActionArea>
        </Card>
    </Grid2>
}

const PackageDialog = () => {
    const { response, makeRequest, error, cancelRequest } = useApiHook();
    const { isOpen, closeModal } = usePackageModal();
    const [packages, setPackages] = useState([]);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [modalStatus, setModalStatus] = useState(MODAL_STATUS.loading);
    const userCtx = useUser();
    const { isNativePlatform } = useAppView();

    useEffect(() => {
        if (isOpen) {
            setModalStatus(MODAL_STATUS.loading);
            setPackages([]);
        }
        makeRequest('/billing/packages');
    }, [isOpen]);


    // Web - post message listener
    useEffect(() => {
        const handler = (event) => {
            if (event.origin !== config.FRONTEND_ORIGIN) return;

            if (event.data?.type === 'STRIPE_CHECKOUT_SUCCESS') {
                onCheckoutSuccess();
            }
        };

        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, []);

    // Mobile - deep link listener
    useEffect(() => {
        const removeListener = App.addListener('appUrlOpen', (event) => {
            if (event.url.includes("/checkout/success")) {
                onCheckoutSuccess();
            }
        });

        return () => {
            if (typeof removeListener === 'function') removeListener();
        }
    }, [])

    useEffect(() => {
        if (response) {
            if (response.state === 'success') {
                if (response.request && response.request.requestType === 'checkout') {
                    const width = 500;
                    const height = 700;

                    const left = window.screenX + (window.outerWidth - width) / 2;
                    const top = window.screenY + (window.outerHeight - height) / 2;

                    if (isNativePlatform) {
                        Browser.open({
                            url: response.data.url,
                            presentationStyle: "fullscreen"
                        });
                    } else {
                        window.open(
                            response.data.url,
                            'stripeCheckout',
                            `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars=yes`
                        );
                    }
                    return;
                }
                const packagesList = response.data;
                setPackages(packagesList);
                setModalStatus(MODAL_STATUS.packageList);
            }
        }
        if (error) {

        }
    }, [error, isNativePlatform, response]);

    const onCloseHandler = () => {
        cancelRequest();
        closeModal();
    }

    const onPackageClickHandler = (id) => {
        setSelectedPackage(id);
        setModalStatus(MODAL_STATUS.checkout);
    }

    const onBackBtnClickHandler = () => {
        cancelRequest();
        setModalStatus(MODAL_STATUS.packageList);
    }

    const onPayNowBtnClickHandler = () => {
        setModalStatus(MODAL_STATUS.paying);
        makeRequest("/billing/checkout", "post", { priceId: selectedPackage, requestType: 'checkout' });
    }

    const onCheckoutSuccess = () => {
        userCtx.fetchUsage();
        setModalStatus(MODAL_STATUS.success);
    }

    const packagesList = packages.map(p => {
        const { id, name, description, price, aiTokensLimit, secondsLimit } = p;
        return <PackageCard
            key={id}
            id={id}
            name={name}
            description={description}
            price={price}
            tokenLimit={aiTokensLimit}
            secondsLimit={secondsLimit}
            onClick={onPackageClickHandler}
        />;
    });

    let modalTitle = null;
    let modalBody;
    let showBackButton = false;
    switch (modalStatus) {
        case MODAL_STATUS.paying:
            modalTitle = "Pagamento"
            modalBody = (
                <Stack alignItems='center' justifyContent='center' flexDirection='column'>
                    <PaymentIcon sx={{ fontSize: '4rem' }} />
                    <Typography mt={2} textAlign={{ xs: 'center', sm: 'left' }} variant='h6'>In attesa del pagamento...</Typography>
                    <Typography variant='h7' mt={2} textAlign={{ xs: 'center', sm: 'left' }}>Esegui il pagamento sulla finestra che ti apparirà a breve.</Typography>
                </Stack>
            )
            break;
        case MODAL_STATUS.success:
            modalTitle = "Acquisto effettuato"
            modalBody = (
                <Stack alignItems='center' justifyContent='center' flexDirection='column'>
                    <CheckCircleIcon sx={{ fontSize: '4rem' }} color={'success'} />
                    <Typography mt={2} textAlign={{ xs: 'center', sm: 'left' }} color={"success"} variant='h6'>Pagamento andato a buon fine!</Typography>
                    <Typography variant='h7' mt={2} textAlign={{ xs: 'center', sm: 'left' }}>Grazie per aver acquistato un pacchetto crediti!</Typography>
                    <Typography variant='h7' mt={2} textAlign={{ xs: 'center', sm: 'left' }}>Abbiamo aggiunto le ore di sboinatura appena acquistate a quelle che avevi già a disposizione!</Typography>
                </Stack>
            )
            break;
        case MODAL_STATUS.packageList:
            modalTitle = "Pacchetti crediti";
            modalBody = (
                <Stack m={0} p={0}>
                    <Typography variant={'h6'}>Acquista un pacchetto crediti e utilizza Sbobbi senza pensieri!</Typography>
                    <Typography mb={2} variant={'h6'} fontWeight='bold'>Le funzionalità AI saranno disponibili a breve.</Typography>
                    <Grid2 spacing={2} container>
                        {packagesList}
                    </Grid2>
                </Stack>
            );
            break;
        case MODAL_STATUS.checkout:
            const pack = packages.find(p => p.id === selectedPackage);
            modalTitle = "Checkout";
            modalBody = (<>
                <Stack>
                    <Typography variant='h4' textAlign='center' color='primary' fontWeight='bold'>{pack.name}</Typography>
                    <Typography mt={1} textAlign='center' variant='h5'>{pack.description}</Typography>
                    {/* <Typography textAlign='center' variant='h5'>{pack.aiTokensLimit/1000000} milioni di token AI</Typography> */}
                    <Box mt={4} mb={3} display='flex' alignItems='center' justifyContent='center'>
                        <Typography variant='h5'>Prezzo:</Typography>
                        <Typography variant='h4' fontWeight='bold' color="success">€{pack.price}</Typography>
                    </Box>
                    <Button fullWidth size='large' variant='contained' onClick={onPayNowBtnClickHandler}>Paga ora</Button>
                </Stack>
            </>);
            showBackButton = true;
            break;
        case MODAL_STATUS.loading:
        default:
            modalTitle = "Caricamento pacchetti crediti";
            modalBody = <Grid2 container spacing={2}>
                {[...Array(4)].map((_, index) => (
                    <Grid2 key={index} size={{ xs: 12, sm: 6 }}>
                        <Card>
                            <CardContent>
                                <Box textAlign="center">
                                    <Typography mb={1} variant='h4' fontWeight='bold'>
                                        <Skeleton />
                                    </Typography>
                                    <Typography mb={2} variant='body1'>
                                        <Skeleton />
                                    </Typography>
                                    <Typography variant='h5' fontWeight='bold'>
                                        <Skeleton />
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid2>
                ))
                }
            </Grid2>;
    }

    return <>
        <ResponsiveDialog
            title={modalTitle}
            isOpen={isOpen}
            onClose={onCloseHandler}
            preventBackdropClick={true}
            actions={<>
                {showBackButton && <Button sx={{ fontSize: '1.1rem', color: '#666' }} size='large' onClick={onBackBtnClickHandler}>Indietro</Button>}
                <Button sx={{ fontSize: '1.1rem', color: '#666' }} size='large' onClick={onCloseHandler}>{modalStatus === MODAL_STATUS.success ? "Chiudi" : "Annulla"}</Button>
            </>}>
            {modalBody}
        </ResponsiveDialog>
    </>
};

export default PackageDialog;
